/**
 * The size a frame opens at: its card's shape at the opened type (Part 10,
 * Rule 1). Every input is card data (`previewSource`), so the size does not
 * change when the detail lands.
 *
 * - media: the card's aspect fitted into the media box;
 * - reading: a 40em column at 17px, as tall as its content, clamped from
 *   360px to 82% of the surface;
 * - object: the card's em layout at 24px, plus what the window adds below;
 * - the task board: six readable columns and the task pocket's lane,
 *   clamped to the media box;
 * - a connection prompt: its small card, one row per app, never enlarged;
 * - another tool: its created size.
 */
import type { FrameThumbnailTheme } from "@/api/frame-listing";
import { toAgendaItems } from "@/components/shapes/agenda-frame/agenda-items";
import { codeFrameText } from "@/components/shapes/code-frame/code-content";
import { firstCardImage } from "@/components/shapes/image-frame/image-card-content";
import { CARD_PADDING_EM } from "@/components/shapes/shared/frame-content-padding";
import {
  BOARD_OPEN_COLUMN_W,
  KANBAN_COLUMN_GAP,
  KANBAN_COLUMNS,
} from "@/components/shapes/taskboard-frame/kanban-columns";
import { todoItemsFrom } from "@/components/shapes/todo-frame/todo-items";
import {
  cardPreviewEm,
  frameCardShape,
  OBJECT_OPEN_EM,
  OBJECT_OPEN_FS,
  objectExtraEm,
  OPEN_FS,
  READING_OPEN_EM,
} from "@/lib/frame-card-shape";
import { connectionPromptSlugs } from "@/lib/composio";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { resolveCreatedSize } from "@/lib/frame-defaults";
import { previewSource } from "@/lib/frame-preview-source";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import {
  calculateMediaFocusWindowBox,
  READING_H_RATIO,
} from "@/lib/focus/focus-container-boxes";
import type { FocusNaturalSize } from "@/lib/focus/focus-types";
import { TASK_POCKET_LANE } from "@/lib/task-pocket-placement";

/**
 * A connection prompt: its title and reason, then one row per app (icon,
 * name, Connect button). At least the smallest content fit
 * (`canvas-presentation-layout.ts`); an agent presentation grows it to its
 * measured content (`agent-content-refit.ts`).
 */
const CONNECTION_PROMPT_WIDTH = 320;
const CONNECTION_PROMPT_MIN_HEIGHT = 240;
/** The card's padding, title and a reason line. */
const CONNECTION_PROMPT_CHROME_HEIGHT = 120;
const CONNECTION_PROMPT_ROW_HEIGHT = 56;

function connectionPromptSize(frame: FrameData): FocusNaturalSize {
  const rows = Math.max(1, connectionPromptSlugs(previewSource(frame)).length);
  return {
    width: CONNECTION_PROMPT_WIDTH,
    height: Math.max(
      CONNECTION_PROMPT_MIN_HEIGHT,
      CONNECTION_PROMPT_CHROME_HEIGHT + rows * CONNECTION_PROMPT_ROW_HEIGHT,
    ),
    container: "compact",
  };
}
/** The shortest reading column, in px. */
const READING_MIN_HEIGHT = 360;
/** A reading type that fills the column's height (a document, a session). */
const READING_FULL_EM = Number.POSITIVE_INFINITY;
/** `PreviewBody`'s padding, top and bottom. */
const PAD_EM = CARD_PADDING_EM.blockStart + CARD_PADDING_EM.blockEnd;
/** A one-line `PreviewTitle` (1.42em × 1.16, its 0.6em margin) and its gap. */
const TITLE_EM = 3.1;
/** A `PreviewCheckRow` (one 1.45em line) and its 0.6em gap. */
const CHECK_ROW_EM = 2.05;
/** A muted 0.84em detail line under a row (todo and event rows alike). */
const DETAIL_LINE_EM = 1.22;
/** The todo's "x of y done" (0.84em, its 0.4em margin). */
const TODO_FOOTER_EM = 1.55;
/** `DayAccent` (the 2.2em day on the 0.86em weekday) and its 0.9em gap. */
const DAY_ACCENT_EM = 2.85;
/** An `EventRows` row (a 1.3em title over its time) and its gap. */
const EVENT_ROW_EM = 3.32;
const EVENT_GAP_EM = 0.8;
/** The calendar's week strip (two 0.8em lines, padded) and its gap. */
const WEEK_STRIP_EM = 3.86;
/** `CodeWindow`'s padding, top and bottom: 1.5em + 1.5em at 0.8em. */
const CODE_PAD_EM = 2.4;
/** A code line: mono 0.8em at line height 1.7. */
const CODE_LINE_EM = 1.36;

function detailEm(rows: ReadonlyArray<{ detail?: string }>): number {
  return rows.filter((row) => row.detail?.trim()).length * DETAIL_LINE_EM;
}

/**
 * A reading frame's opened content height in em of `OPEN_FS`, from the
 * card's em vocabulary without the card's caps.
 */
function openedReadingHeightEm(frame: FrameData): number {
  const source = previewSource(frame);
  switch (frame.frameType) {
    case "todo": {
      const items = todoItemsFrom(source.items);
      return (
        PAD_EM +
        TITLE_EM +
        items.length * CHECK_ROW_EM +
        detailEm(items) +
        TODO_FOOTER_EM
      );
    }
    case "agenda":
    case "calendar": {
      const calendar = frame.frameType === "calendar";
      const rows = toAgendaItems(calendar ? source.events : source.items) ?? [];
      // The agenda's header shows only when it has a date or a pill.
      const header = calendar || source.date || source.statusPill;
      return (
        PAD_EM +
        (header ? DAY_ACCENT_EM : 0) +
        (rows.length ? rows.length * EVENT_ROW_EM - EVENT_GAP_EM : 0) +
        detailEm(rows) +
        (calendar ? WEEK_STRIP_EM : 0)
      );
    }
    case "code": {
      // The title line, then every line of the code.
      const lines = codeFrameText(source).split("\n").length + 1;
      return CODE_PAD_EM + lines * CODE_LINE_EM;
    }
    default:
      return READING_FULL_EM;
  }
}

/**
 * `aspect` fitted into the media box of `surface`, never wider than
 * `maxWidth` (an image's pixel width, so it is never upscaled).
 */
function mediaSize(
  aspect: number,
  surface: FocusWindowBox,
  maxWidth = Number.POSITIVE_INFINITY,
) {
  const box = calculateMediaFocusWindowBox(surface);
  const width = Math.min(box.width, box.height * aspect, maxWidth);
  return { width: Math.round(width), height: Math.round(width / aspect) };
}

/**
 * The opened task board: the card's inline margin, every column at
 * `BOARD_OPEN_COLUMN_W` with its gaps, then the task pocket's lane, so an
 * open task never covers a column. Clamped to the media box: on a narrower
 * surface the columns narrow, then scroll sideways.
 */
export function taskboardOpenedSize(surface: FocusWindowBox): FocusNaturalSize {
  const box = calculateMediaFocusWindowBox(surface);
  const columns = KANBAN_COLUMNS.length;
  const width =
    CARD_PADDING_EM.inline * OPEN_FS +
    columns * BOARD_OPEN_COLUMN_W +
    (columns - 1) * KANBAN_COLUMN_GAP +
    TASK_POCKET_LANE;
  return {
    width: Math.round(Math.min(width, box.width)),
    height: box.height,
    container: "media",
  };
}

/** The size `frame` opens at on `surface`. */
export function openedNaturalSize(
  frame: FrameData,
  surface: FocusWindowBox,
  theme?: FrameThumbnailTheme,
): FocusNaturalSize {
  const shape = frameCardShape(frame, theme);
  switch (shape.family) {
    case "media":
      return {
        ...mediaSize(
          shape.aspect ?? 1,
          surface,
          frame.frameType === "image"
            ? (firstCardImage(previewSource(frame).images)?.width ?? undefined)
            : undefined,
        ),
        container: "media",
      };
    case "reading": {
      const height = openedReadingHeightEm(frame) * OPEN_FS;
      return {
        width: READING_OPEN_EM * OPEN_FS,
        height: Math.round(
          Math.min(
            Math.max(height, READING_MIN_HEIGHT),
            surface.height * READING_H_RATIO,
          ),
        ),
        container: "reading",
      };
    }
    case "object": {
      const cardEm = shape.aspect
        ? OBJECT_OPEN_EM / shape.aspect
        : cardPreviewEm(frame.frameType);
      const extraEm = objectExtraEm(frame.frameType, previewSource(frame));
      return {
        width: Math.round(OBJECT_OPEN_EM * OBJECT_OPEN_FS),
        height: Math.round((cardEm + extraEm) * OBJECT_OPEN_FS),
        container: "object",
      };
    }
    default: {
      if (frame.frameType === "taskboard") return taskboardOpenedSize(surface);
      if (frame.frameType === "connection_prompt") {
        return connectionPromptSize(frame);
      }
      const size = resolveCreatedSize(frame.frameType, {
        content: previewSource(frame),
      });
      return { width: size.w, height: size.h };
    }
  }
}

/** List-like reading types: their rows fit a narrow column. */
const COMPACT_READING_TYPES = new Set(["todo", "agenda", "calendar"]);
/** That column, in em of `OPEN_FS`. */
const COMPACT_READING_EM = 24;
/** The shortest reading frame in an agent round, in px. */
const ROUND_READING_MIN_HEIGHT = 200;
/** `PreviewTitle`: 1.42em at line height 1.16, at most two lines. */
const TITLE_FONT_EM = 1.42;
const TITLE_LINE_EM = TITLE_FONT_EM * 1.16;
/** An average glyph's advance in em of its font, to estimate wrapping. */
const AVERAGE_GLYPH_EM = 0.55;

/**
 * The lines a narrow column adds. Rows, details and event lines truncate
 * to one line (`card-preview-parts.tsx`, `card-visual-parts.tsx`); only a
 * todo's title wraps, to a second line, when it is longer than the column.
 */
function narrowColumnExtraEm(frame: FrameData, widthEm: number): number {
  if (frame.frameType !== "todo") return 0;
  const textEm = widthEm - CARD_PADDING_EM.inline * 2;
  const perLine = Math.max(
    1,
    Math.floor(textEm / (TITLE_FONT_EM * AVERAGE_GLYPH_EM)),
  );
  return frameDisplayTitle(frame).trim().length > perLine ? TITLE_LINE_EM : 0;
}

/**
 * The size `frame` takes in an agent round (plan 13): its opened size, but a
 * reading frame is only as tall as its content and a list-like one (todo,
 * agenda, calendar) a narrow column, so a three-row checklist stays small.
 */
export function roundNaturalSize(
  frame: FrameData,
  surface: FocusWindowBox,
): FocusNaturalSize {
  const opened = openedNaturalSize(frame, surface);
  if (opened.container !== "reading") return opened;
  const compact = COMPACT_READING_TYPES.has(frame.frameType);
  const heightEm =
    openedReadingHeightEm(frame) +
    (compact ? narrowColumnExtraEm(frame, COMPACT_READING_EM) : 0);
  return {
    width: compact ? COMPACT_READING_EM * OPEN_FS : opened.width,
    height: Math.round(
      Math.min(
        Math.max(heightEm * OPEN_FS, ROUND_READING_MIN_HEIGHT),
        surface.height * READING_H_RATIO,
      ),
    ),
    container: "reading",
  };
}
